import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import styles from "./authForm.module.css";

export default function ForgotPassword() {
  const baseURL = process.env.REACT_APP_BACKEND_API_URL;
  if (!baseURL) {
    throw new Error(
      "Missing REACT_APP_BACKEND_API_URL. Set it in Coolify before building."
    );
  }

  const navigate = useNavigate();
  const [step, setStep] = useState<"email" | "reset">("email");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSendCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    setLoading(true);

    try {
      const res = await fetch(`${baseURL}api/auth/forgot-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.error || "Failed to send reset code");
      }

      setSuccess(data.message || "Password reset code sent to your email");
      setStep("reset");
    } catch (err: any) {
      setError(err.message || "Failed to send reset code. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (newPassword !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    if (newPassword.length < 8) {
      setError("Password must be at least 8 characters long");
      return;
    }

    setLoading(true);

    try {
      const res = await fetch(`${baseURL}api/auth/confirm-forgot-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email,
          code,
          newPassword,
        }),
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.error || "Failed to reset password");
      }

      setSuccess("Password reset successfully. Taking you to the login page.");
      setTimeout(() => {
        navigate("/login");
      }, 2000);
    } catch (err: any) {
      setError(
        err.message ||
          "Failed to reset password. Please check your code and try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const alerts = (
    <>
      {error && (
        <p className="ui-banner ui-banner--error" role="alert">
          {error}
        </p>
      )}
      {success && (
        <p className="ui-banner ui-banner--success" role="status">
          {success}
        </p>
      )}
    </>
  );

  return (
    <div className={styles.pane}>
      <header className={styles.head}>
        <h2 className={styles.title}>
          {step === "email" ? "Reset your password" : "Choose a new password"}
        </h2>
        <p className={styles.lead}>
          {step === "email"
            ? "Enter your email address and we will send you a code."
            : `Enter the code we sent to ${email}, then set a new password.`}
        </p>
      </header>

      {step === "email" ? (
        <form onSubmit={handleSendCode} className={styles.form} >
          {alerts}

          <div className="ui-field">
            <label className="ui-label" htmlFor="email">
              Email
            </label>
            <input
              id="email"
              className="ui-input"
              type="email"
              autoComplete="email"
              inputMode="email"
              required
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={loading}
            />
          </div>

          <button
            type="submit"
            className={`ui-btn ui-btn--lg ui-btn--full ${styles.submit}`}
            disabled={loading || !email}
            aria-busy={loading}
          >
            {loading ? "Sending..." : "Send reset code"}
          </button>
        </form>
      ) : (
        <form onSubmit={handleResetPassword} className={styles.form} >
          {alerts}

          <div className="ui-field">
            <label className="ui-label" htmlFor="code">
              Verification code
            </label>
            <input
              id="code"
              className={`ui-input ${styles.code}`}
              type="text"
              autoComplete="one-time-code"
              inputMode="numeric"
              maxLength={6}
              required
              placeholder="000000"
              value={code}
              onChange={(e) =>
                setCode(e.target.value.replace(/[^0-9]/g, "").slice(0, 6))
              }
              disabled={loading}
            />
            <p className="ui-help">Six digits, from the email we just sent.</p>
          </div>

          <div className="ui-field">
            <label className="ui-label" htmlFor="newPassword">
              New password
            </label>
            <div className={styles.secretField}>
              <input
                id="newPassword"
                className={`ui-input ${styles.secretInput}`}
                type={showPassword ? "text" : "password"}
                autoComplete="new-password"
                required
                minLength={8}
                placeholder="At least 8 characters"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                disabled={loading}
              />
              <button
                type="button"
                className={styles.reveal}
                aria-label={showPassword ? "Hide password" : "Show password"}
                onClick={() => setShowPassword((v) => !v)}
              >
                {showPassword ? "Hide" : "Show"}
              </button>
            </div>
          </div>

          <div className="ui-field">
            <label className="ui-label" htmlFor="confirmPassword">
              Confirm new password
            </label>
            <input
              id="confirmPassword"
              className="ui-input"
              type="password"
              autoComplete="new-password"
              required
              minLength={8}
              placeholder="Repeat the new password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              disabled={loading}
              aria-invalid={
                confirmPassword.length > 0 && confirmPassword !== newPassword
              }
            />
            {confirmPassword.length > 0 && confirmPassword !== newPassword && (
              <p className="ui-error-text">Both passwords must match.</p>
            )}
          </div>

          <button
            type="submit"
            className={`ui-btn ui-btn--lg ui-btn--full ${styles.submit}`}
            disabled={loading || !code || !newPassword || !confirmPassword}
            aria-busy={loading}
          >
            {loading ? "Resetting..." : "Reset password"}
          </button>
        </form>
      )}

      <div className={styles.links}>
        {step === "reset" && (
          <p>
            No code yet?{" "}
            <button
              type="button"
              className="ui-link"
              onClick={() => {
                setStep("email");
                setCode("");
                setNewPassword("");
                setConfirmPassword("");
                setError("");
                setSuccess("");
              }}
            >
              Send it again
            </button>
          </p>
        )}
        <p>
          Remembered it?{" "}
          <Link to="/login" className="ui-link">
            Log in
          </Link>
        </p>
      </div>
    </div>
  );
}
